import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { Observable } from "rxjs";
import { map, take } from "rxjs/operators";
import { policyTypeGroups } from "src/app/core/constants/policy.constants";
import { Routes } from "src/app/core/constants/routes.constants";
import { Policy } from "src/app/core/models/policy";
import { fromPolicy, State } from "src/app/store";

const routeSegments = new Map([
  ["auto", policyTypeGroups.auto],
  ["boat", policyTypeGroups.boat],
  ["business", policyTypeGroups.commercialBusiness],
  ["coml-auto", policyTypeGroups.commercialAuto],
  ["coml-excess", policyTypeGroups.commercialExcess],
  ["coml-package", policyTypeGroups.commercialPackage],
  ["dwelling-fire", policyTypeGroups.dwellingFire],
  ["excess", policyTypeGroups.excess],
  ["farm-auto", policyTypeGroups.farmAuto],
  ["farm-excess", policyTypeGroups.farmExcess],
  ["farm-package", policyTypeGroups.farmPackage],
  ["home", policyTypeGroups.home],
  ["inland-marine", policyTypeGroups.inlandMarine],
]);

function getRouteSegment(policyNumber: string): string {
  if (policyNumber) {
    for (const [key, value] of routeSegments) {
      if (value.find((x) => policyNumber.startsWith(x))) {
        return key;
      }
    }
  }
  return null;
}

function getNextPolicyNumber(policies: Policy[], policyNumber: string): string {
  const valid = isValid(policies, policyNumber);
  return valid ? policyNumber : policies[0]?.policyNumber;
}

function hasRouteSegment(policyNumber: string): boolean {
  return !!getRouteSegment(policyNumber);
}

function isValid(policies: Policy[], policyNumber: string): boolean {
  if (policyNumber) {
    return (
      policies.findIndex((p) => p.policyNumber === policyNumber.toUpperCase()) >
      -1
    );
  } else {
    return false;
  }
}

function byStatusAscPolicyNumberAsc(a: Policy, b: Policy): number {
  return (
    (a.cancelled ? 1 : 0) - (b.cancelled ? 1 : 0) ||
    a.policyNumber.localeCompare(b.policyNumber)
  );
}


@Injectable({
  providedIn: "root",
})
export class PoliciesGuard implements CanActivate {
  constructor(private router: Router, private store: Store<State>) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.store.select(fromPolicy.selectors.selectPresentPolicies).pipe(
      take(1),
      map(policies => policies.sort(byStatusAscPolicyNumberAsc)),
      map((policies) => {
        const policyNumber = getNextPolicyNumber(
          policies,
          route.queryParams.policyNumber
        );

        if (hasRouteSegment(policyNumber)) {
          return this.router.createUrlTree([
            Routes.policies.path,
            getRouteSegment(policyNumber),
            policyNumber,
          ]);
        } else {
          return this.router.createUrlTree([Routes.home.path]);
        }
      })
    );
  }
}
